// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/recorrido/RecorridoNSL.jsx
//  EL RECORRIDO DE BIENVENIDA — SPACY HABLANDO, NO UN TUTORIAL ENCIMA
//
//  Rob, 26-09-2026: «lo ideal es mandarlo a Spacy Coach, que detecte que es la
//  primera vez y ahí en el chat le explique al alumno…».
//
//  ── NADA DE ESTO SALE DEL CHAT. NI EL MENÚ ─────────────────────────────────
//  Costó dos correcciones suyas aprenderlo, así que queda escrito aquí:
//
//    1ª  «Pensé que esto se vería dentro del chat y que quedarían sus cards
//         dentro del chat… porque esto se ve aburrido y sin efecto wow, como
//         lo hemos hecho con el coach».  → los pasos dejaron de ser tarjetas
//         propias y pasaron a ser burbujas suyas.
//    2ª  «Al dar clic en el botón quiero ver las opciones dentro del chat, no
//         fuera».  → el menú, que yo había dejado en una capa flotante
//         «porque no cabía», también entró.
//
//  Mi argumento para sacarlo era que once opciones no caben en el panel de la
//  charla. Era verdad y era irrelevante: la charla TIENE scroll, como cualquier
//  conversación, y lo que había que hacer no era sacar el menú de ahí sino
//  hacerle sitio —el Coach pliega su botonera cuando el recorrido habla—. Una
//  capa flotante resuelve el problema del espacio rompiendo lo único que hacía
//  especial a esto: que todo se lo cuenta Spacy, en su sitio.
//
//  ── APARTE DEL COACH ───────────────────────────────────────────────────────
//  `CoachPage.jsx` son 1.319 líneas con 34 estados. El Coach sólo dice CUÁNDO
//  empieza el recorrido, presta sus clases de burbuja y mueve el avatar cuando
//  esto le avisa de que Spacy está hablando. Qué paso toca, qué suena y si se
//  omitió es asunto de esta carpeta.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { SpacyThinkingBubble } from '../SpacyScanner';
// ── LOS SONIDOS SALEN DEL MOTOR DEL COACH, NO DE UNO PROPIO ────────────────
//  `src/audio/fx.js` es el que ya suena en esta pantalla, y trae dos cosas que
//  un motor nuevo no tendría: el silenciador del alumno y —esto es lo que
//  importa aquí— el aviso de que hay VOZ EN CURSO, que apaga los efectos para
//  que no pisen a Spacy mientras habla. Un pitido encima de su voz suena a
//  fallo, no a interfaz.
//
//  Y se respeta su regla: sólo suena lo que AVANZA algo. Abrir el menú no
//  suena; empezar, pasar de paso y terminar, sí. Un sonido en cada toque deja
//  de ser información en diez minutos y pasa a ser ruido que el alumno
//  silencia — y al silenciarlo se lleva por delante también los aciertos.
import { fxStart, fxAvanzar, fxFinish } from '../../audio/fx';
//  Rob, 29-09-2026, repasando el alta de un alumno nuevo: «cuando llega al
//  100% deberia salir confeti». Es el unico logro de toda la aplicacion que el
//  alumno consigue ANTES de entrenar, y hasta ahora terminaba igual que pasar
//  de pagina. El componente ya existe y se apaga solo con
//  `prefers-reduced-motion`, asi que no hay que inventar nada.
import Confeti from '../Confeti';
import { SALUDO, PASOS, CIERRE, MENU, TOTAL_PASOS } from './guion';
import { habla, hayVoz } from './voz';
import {
    leeRecorrido, marcaSaludado, guardaPaso, marcaTerminado, marcaOmitido, reinicia,
} from './estado';
import './RecorridoNSL.css';

// Lo que se lee mientras «piensa» entre un paso y el siguiente.
//
// ⚠️  El formato NO es libre: `SpacyThinkingBubble` lee `msg.icon` y `msg.text`.
//     El primer intento le pasó una lista de textos sueltos y la tarjeta salía
//     vacía —sin romper nada, sólo en blanco—, que es el peor de los fallos:
//     parece que la pantalla se quedó colgada.
const PENSANDO = [
    { icon: '🧭', text: 'Te cuento lo siguiente…' },
    { icon: '⚡', text: 'Un segundo…' },
];

// Cuánto tarda en pensar. Suficiente para que se lea como un gesto de alguien
// que habla, poco para que no estorbe: son once pasos, y cada medio segundo de
// más son cinco segundos y medio de espera en el recorrido entero.
const PIENSA_MS = 650;

/**
 * @param {'lineal'|'menu'} modo
 * @param {() => void} onCerrar
 * @param {(hablando:boolean) => void} onHabla  el Coach lo engancha a su estado
 *        `hablando`, que es lo que cambia el vídeo del avatar. Sin esto, Spacy
 *        contaba el recorrido con la cara quieta.
 */
export default function RecorridoNSL({ modo = 'lineal', onCerrar, onHabla }) {
    const navegar = useNavigate();
    // ── «VERLO AHORA» NO SE OFRECE SI YA ESTÁS AHÍ ──────────────────────
    //  Rob, sobre el primer paso: «en el caso de "Verlo ahora" es la misma
    //  página, por lo tanto está de más».
    //
    //  El recorrido se cuenta DENTRO del Coach, así que el paso que explica el
    //  Coach ofrecía un enlace al Coach. No rompe nada y por eso no salía en
    //  ninguna prueba, pero un botón que no lleva a ninguna parte le enseña al
    //  alumno que los botones de esta pantalla no hacen lo que dicen.
    //
    //  Se compara con la dirección de verdad, no con el `id` del paso: mañana
    //  el recorrido puede vivir en otra pantalla y esto seguirá acertando.
    const donde = useLocation();
    const guardado = leeRecorrido();

    const [confeti, setConfeti] = useState(false);
    const [fase, setFase] = useState(() => {
        if (modo === 'menu') return 'menu';
        return guardado.saludado ? 'paso' : 'saludo';
    });
    const [indice, setIndice] = useState(() => Math.min(guardado.paso, TOTAL_PASOS - 1));
    // ── LOS PASOS DICHOS, PERO SE ENSEÑA EL ÚLTIMO ───────────────────────
    //  Nacieron para apilarse: la idea era que esto pareciera una conversación
    //  y no una tarjeta que se reescribe encima de sí misma.
    //
    //  Rob, 30-09-2026, viéndolo con los once pasos: «están saliendo las cards
    //  en escalera y está creciendo enormemente, lo ideal es poner uno a uno y
    //  se quiten los anteriores». Tenía razón, y lo que fallaba era la
    //  analogía: en un chat los turnos son cortos y se leen de un vistazo;
    //  aquí cada paso es un párrafo con título, icono y dos botones, así que
    //  once apilados no son una conversación, son una escalera que empuja los
    //  mandos fuera de la pantalla justo cuando hay que pulsarlos.
    //
    //  La lista se mantiene entera —hace falta para saber qué se ha dicho y
    //  para el amarillo del menú— pero se pinta sólo el último.
    const [dichos, setDichos] = useState([]);
    const [pensando, setPensando] = useState(false);
    // Cuál se está oyendo, para que su botón diga «Sonando…» y no invite a
    // pulsarlo otra vez encima.
    const [suena, setSuena] = useState(null);
    // Los que ya ha visto, para pintarlos en amarillo en el menú. Se arranca
    // de lo guardado y se va sumando: así el amarillo sigue ahí mañana.
    const [vistos, setVistos] = useState(() => guardado.vistos || []);

    const callar = useRef(null);
    const final = useRef(null);
    const menu = useRef(null);
    const temporizador = useRef(null);
    const arrancado = useRef(false);

    // Que lo último dicho quede a la vista: dentro del coach esta conversación
    // nace al final de otra que ya está desplazada abajo.
    useEffect(() => {
        const id = setTimeout(() => {
            // ── EL MENÚ SE COLOCA POR SU TÍTULO, NO POR EL FONDO ─────────
            //  El coach mantiene la charla pegada abajo mientras el alumno no
            //  haya subido a releer (CoachPage.jsx, «EL AUTO-SCROLL, PEGADO AL
            //  FONDO»), y eso está bien para un chat. Pero el menú no es un
            //  mensaje más: si encima de él hay otro turno, pegarse al fondo
            //  deja su título fuera y lo que se ve es un trozo de rejilla sin
            //  encabezado. Parece roto.
            //
            //  La forma de convivir con su auto-scroll sin tocarlo es usar su
            //  propia regla: mueve la charla SOLO si el alumno está abajo, y
            //  se entera por el evento `scroll`. Asignar `scrollTop` a pelo lo
            //  dispara al instante, así que él mismo concluye «ha subido a
            //  mirar algo» y suelta. Con `scrollIntoView` no funcionaba: el
            //  desplazamiento suave tarda, y su observador ganaba a mitad.
            if (fase === 'menu' && menu.current) {
                const caja = menu.current.closest('.cch-charla');
                if (caja) caja.scrollTop = Math.max(0, menu.current.offsetTop - 6);
                else menu.current.scrollIntoView({ block: 'start' });
                return;
            }
            final.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 180);
        return () => clearTimeout(id);
    }, [fase, dichos.length, pensando]);

    // Al desmontar se corta todo: ni la voz ni un temporizador pueden seguir
    // vivos en una pantalla que el alumno ya abandonó.
    useEffect(() => () => {
        callar.current?.();
        if (temporizador.current) clearTimeout(temporizador.current);
        onHabla?.(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Volver a oír un bloque. Rob lo pidió desde el principio: «que se pueda
     * reproducir las veces que sean necesarias». Y sirve para algo más: es la
     * salida cuando el navegador bloqueó el primer intento.
     */
    const repite = (id) => {
        callar.current?.();
        callar.current = habla(id, (hablando) => {
            setSuena(hablando ? id : null);
            onHabla?.(hablando);
        });
    };

    /** Spacy piensa un momento y suelta el paso `i`. */
    const dice = (i) => {
        callar.current?.();
        setPensando(true);
        if (temporizador.current) clearTimeout(temporizador.current);
        temporizador.current = setTimeout(() => {
            setPensando(false);
            setDichos((d) => (d.includes(i) ? d : [...d, i]));
            setIndice(i);
            guardaPaso(i);
            setVistos((v) => (v.includes(i) ? v : [...v, i]));
            const p = PASOS[i];
            if (p) repite(p.id);
        }, PIENSA_MS);
    };

    // ── QUIEN VUELVE A MEDIO RECORRIDO TIENE QUE OÍR ALGO ────────────────
    //  Rob abrió el coach y se encontró la caja de la conversación VACÍA, con
    //  sólo la barra amarilla de avance dentro. No era un fallo de pintado:
    //  quien ya había pasado del saludo entraba en la fase «paso» sin que
    //  nadie dijera el paso guardado, así que no había ninguna burbuja que
    //  enseñar. La barra sola, sin texto, parece la pantalla rota.
    //
    //  Y el guardia se suelta al limpiar. En desarrollo React monta, desmonta
    //  y vuelve a montar a propósito: el desmontaje cancela el temporizador de
    //  `dice()`, y si el guardia siguiera puesto el segundo montaje no volvería
    //  a intentarlo. La burbuja se quedaba en «Un segundo…» para siempre, que
    //  es la misma pantalla rota de antes con otra cara.
    useEffect(() => {
        if (arrancado.current) return undefined;
        if (fase !== 'paso' || dichos.length > 0) return undefined;
        arrancado.current = true;
        dice(indice);
        return () => { arrancado.current = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fase]);

    // ── Y LA BIENVENIDA SE OYE AL LLEGAR ─────────────────────────────────
    //  Es el unico bloque que no lo suelta `dice()`, porque no es un paso:
    //  esta ahi desde que se abre la pantalla.
    //
    //  Puede que el navegador lo bloquee —muchos no dejan sonar nada hasta que
    //  el alumno toca algo— y no pasa nada: el saludo se lee igual, y en
    //  cuanto pulsa «Comencemos» ya hay gesto y el resto del recorrido suena.
    useEffect(() => {
        if (fase !== 'saludo') return undefined;
        const corta = habla('saludo', (hablando) => {
            setSuena(hablando ? 'saludo' : null);
            onHabla?.(hablando);
        });
        callar.current = corta;
        return () => corta?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const empieza = () => {
        fxStart();
        marcaSaludado();
        arrancado.current = true;
        setFase('paso');
        dice(indice);
    };

    const siguiente = () => {
        if (indice >= TOTAL_PASOS - 1) {
            fxFinish();
            marcaTerminado();
            setFase('cierre');
            //  El confeti va AQUI y no en el boton del cierre: este es el
            //  instante en que la barra llega al 100%, y una celebracion que
            //  llega un toque despues del logro ya no celebra nada.
            //
            //  Se apaga solo: `onFin` lo quita cuando acaba la animacion, para
            //  que no siga montado el resto de la sesion.
            setConfeti(true);
            repite('cierre');
            return;
        }
        fxAvanzar();
        dice(indice + 1);
    };

    const para = () => {
        callar.current?.();
        if (temporizador.current) clearTimeout(temporizador.current);
        setSuena(null);
        onHabla?.(false);
    };

    const omite = () => {
        marcaOmitido(indice);
        para();
        onCerrar?.();
    };

    const vePantalla = (ruta) => {
        if (!ruta) return;
        para();
        marcaTerminado();
        navegar(ruta);
    };

    /** Abrir el menú NO borra lo dicho: sigue siendo la misma conversación. */
    const abreMenu = () => { para(); setFase('menu'); };

    const eligeDelMenu = (i) => {
        // ⚠️  Esta linea NO es de adorno. Sin ella, al pasar a la fase «paso»
        //     se disparaba el arranque en frio de arriba y soltaba el paso
        //     GUARDADO, cancelando el que el alumno acababa de elegir: pedias
        //     «Los minijuegos» y Spacy te contaba por donde ibas ayer.
        arrancado.current = true;
        fxAvanzar();
        setFase('paso');
        dice(i);
    };

    const empiezaDeNuevo = () => {
        reinicia();
        setVistos([]);
        setDichos([]);
        setIndice(0);
        arrancado.current = true;
        setFase('saludo');
    };

    /* ── TODO VIVE EN EL HILO ──────────────────────────────────────────────
       Mismas clases que las burbujas del coach: la idea es que no se note la
       diferencia entre esto y lo que Spacy dice el resto del tiempo.         */

    return (
        <div className="rnsl-charla" data-prueba={`recorrido-${fase}`}>
            {confeti && (
                <Confeti piezas={70} onFin={() => setConfeti(false)} />
            )}

            {fase === 'saludo' && (
                <Burbuja titulo={`${SALUDO.titulo} ${SALUDO.bloque}`} texto={SALUDO.texto}
                         onOir={hayVoz('saludo') ? () => repite('saludo') : null}
                         sonando={suena === 'saludo'} prueba="saludo" />
            )}

            {fase !== 'saludo' && dichos.slice(-1).map((i) => (
                <Burbuja
                    key={PASOS[i].id}
                    icono={PASOS[i].icono}
                    titulo={PASOS[i].titulo}
                    texto={PASOS[i].texto}
                    cuenta={`${i + 1} de ${TOTAL_PASOS}`}
                    onVer={PASOS[i].ruta && PASOS[i].ruta !== donde.pathname
                        ? () => vePantalla(PASOS[i].ruta) : null}
                    onOir={hayVoz(PASOS[i].id) ? () => repite(PASOS[i].id) : null}
                    sonando={suena === PASOS[i].id}
                    prueba={PASOS[i].id}
                />
            ))}

            {fase === 'cierre' && (
                <Burbuja titulo={`${CIERRE.titulo} ${CIERRE.bloque}`} texto={CIERRE.texto}
                         onOir={hayVoz('cierre') ? () => repite('cierre') : null}
                         sonando={suena === 'cierre'} prueba="cierre" />
            )}

            {/* ── EL MENÚ, DENTRO DEL HILO ─────────────────────────────────
                Rob lo vio en una columna estrecha con barra de desplazamiento
                y dijo: «el diseño se ve en escalera, se ve espantoso, sin
                efecto wow y muy anticuado; recuerda que es lo primero que ve
                un usuario».

                Tenía razón. Once filas de una sola columna, dentro de una
                burbuja que ocupa el 86% del ancho, son literalmente una
                escalera: el ojo baja peldaño a peldaño y no ve el conjunto.
                Y un menú existe para verse ENTERO de un vistazo.

                Ahora es una rejilla de fichas —icono, la palabra suelta y el
                nombre completo debajo— que se reparte en tres columnas en un
                teléfono y en más cuando hay ancho. Las once caben sin rodar, y
                entran escalonadas, una detrás de otra, en vez de aparecer de
                golpe.                                                       */}
            {fase === 'menu' && (
                <div className="cch-fila rnsl-fila rnsl-fila-ancha" ref={menu}>
                    <div className="cch-turno cch-turno-coach rnsl-burbuja rnsl-burbuja-ancha"
                         data-prueba="recorrido-menu-burbuja">
                        <strong className="rnsl-titulo">
                            <span className="rnsl-icono" aria-hidden="true">🧭</span>
                            {MENU.titulo} {MENU.bloque}
                        </strong>

                        <ul className="rnsl-rejilla">
                            {PASOS.map((p, i) => (
                                <li key={p.id}>
                                    <button
                                        type="button"
                                        className={`rnsl-ficha${vistos.includes(i) ? ' rnsl-ficha-vista' : ''}`}
                                        /* El escalonado va aquí y no en el CSS
                                           porque depende de la posición, y son
                                           once: escribirlo a mano son once
                                           reglas que nadie mantiene. */
                                        style={{ animationDelay: `${i * 35}ms` }}
                                        data-prueba={`recorrido-opcion-${p.id}`}
                                        onClick={() => eligeDelMenu(i)}
                                    >
                                        <span className="rnsl-ficha-icono" aria-hidden="true">{p.icono}</span>
                                        <span className="rnsl-ficha-nombre">{p.etiqueta}</span>
                                        <span className="rnsl-ficha-sub">{p.titulo}</span>
                                        {/* La marca de visto se DICE, no sólo
                                            se pinta: quien no distingue el
                                            amarillo se queda sin la mitad del
                                            menú. */}
                                        {vistos.includes(i) && (
                                            <span className="rnsl-visto" aria-label="ya lo has visto">✓</span>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {pensando && (
                // El gancho es para la bateria: "sigue pensando" es un estado
                // que hay que poder VER desde fuera, porque quedarse ahi es el
                // fallo, no un paso intermedio.
                <span data-prueba="recorrido-pensando" hidden />
            )}
            {pensando && (
                <SpacyThinkingBubble
                    isActive
                    mensajes={PENSANDO}
                    avatarIcon={<span aria-hidden="true">🤖</span>}
                />
            )}

            {/* ── LOS MANDOS ────────────────────────────────────────────────
                Rob: «el botón de continuar y cancelar ocupan demasiado espacio
                y se vuelven los protagonistas, cuando el diseño debería ser la
                información».

                Tenía razón: un botón amarillo a todo el ancho y con halo pesa
                más que el párrafo que hay encima, y el párrafo es lo que el
                alumno ha venido a leer. Ahora es un botón del tamaño de su
                texto, alineado a la izquierda bajo la burbuja, y la barra de
                avance es un hilo. Se ven, no gritan.

                Mientras piensa NO se esconden, se DESACTIVAN. Esconderlos
                dejaba la conversación saltando arriba y abajo cada vez que
                Spacy tomaba aire, y en un teléfono ese salto mueve de sitio el
                botón justo cuando el dedo ya va hacia él.                    */}
            <div className="rnsl-mandos">
                {fase === 'paso' && (
                    <div className="rnsl-barra" aria-hidden="true">
                        <div className="rnsl-barra-llena"
                             style={{ width: `${((indice + 1) / TOTAL_PASOS) * 100}%` }} />
                    </div>
                )}

                <div className="rnsl-botones">
                    {fase === 'saludo' && (
                        <>
                            <button type="button" className="rnsl-boton"
                                    data-prueba="recorrido-empezar" onClick={empieza}>
                                {SALUDO.boton} →
                            </button>
                            <button type="button" className="rnsl-secundario"
                                    data-prueba="recorrido-omitir" onClick={omite}>
                                {SALUDO.omitir}
                            </button>
                        </>
                    )}

                    {fase === 'paso' && (
                        <>
                            <button type="button" className="rnsl-boton"
                                    data-prueba="recorrido-continuar" disabled={pensando}
                                    onClick={siguiente}>
                                {indice >= TOTAL_PASOS - 1 ? 'Terminar' : 'Continuar'} →
                            </button>
                            {/* La puerta al menú, en el sitio donde el alumno
                                ya está mirando. Antes sólo se llegaba por el
                                botón de la botonera, que queda debajo. */}
                            <button type="button" className="rnsl-secundario"
                                    data-prueba="recorrido-ver-todo" disabled={pensando}
                                    onClick={abreMenu}>
                                Ver todo
                            </button>
                            <button type="button" className="rnsl-secundario"
                                    data-prueba="recorrido-omitir" disabled={pensando}
                                    onClick={omite}>
                                Omitir
                            </button>
                        </>
                    )}

                    {fase === 'menu' && (
                        <>
                            <button type="button" className="rnsl-secundario"
                                    data-prueba="recorrido-de-nuevo" onClick={empiezaDeNuevo}>
                                Verlo entero otra vez
                            </button>
                            {/* En rojo porque lo pidió Rob, y porque es la
                                única salida del menú que no lleva a ninguna
                                parte: conviene que se distinga de la que sí. */}
                            <button type="button" className="rnsl-secundario rnsl-cerrar"
                                    data-prueba="recorrido-cerrar"
                                    onClick={() => { para(); onCerrar?.(); }}>
                                Cerrar
                            </button>
                        </>
                    )}

                    {fase === 'cierre' && (
                        <>
                            {/* Termina DENTRO del producto, no aplaudiendo, y
                                sin mudarse de pantalla: cierra el recorrido y
                                deja al alumno en la conversación del coach, que
                                es donde ha estado todo el rato.

                                Va solo. El «Ahora no» de antes tenía sentido
                                cuando el botón se lo llevaba a otra parte: era
                                la forma de decir «no me muevas». Ahora los dos
                                hacen lo mismo, y dos botones para una sola
                                salida no dan a elegir, dan que pensar. */}
                            <button type="button" className="rnsl-boton"
                                    data-prueba="recorrido-entrenar"
                                    onClick={() => {
                                        para();
                                        marcaTerminado();
                                        onCerrar?.({ arrancar: true });
                                    }}>
                                {CIERRE.boton} →
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div ref={final} />
        </div>
    );
}

/**
 * Una burbuja de Spacy. Usa las clases del coach (`cch-fila`, `cch-turno`,
 * `cch-turno-coach`) a propósito: si el recorrido se pintara con estilo propio,
 * volvería a parecer un tutorial pegado encima en vez de alguien hablando.
 */
function Burbuja({ icono, titulo, texto, cuenta, onVer, onOir, sonando, prueba }) {
    return (
        <div className="cch-fila rnsl-fila">
            <div className="cch-turno cch-turno-coach rnsl-burbuja"
                 data-prueba={prueba ? `recorrido-burbuja-${prueba}` : undefined}>
                {cuenta && (
                    <span className="rnsl-cuenta" data-prueba="recorrido-cuenta">{cuenta}</span>
                )}
                <strong className="rnsl-titulo">
                    {icono && <span className="rnsl-icono" aria-hidden="true">{icono}</span>}
                    {titulo}
                </strong>
                <p className="rnsl-dicho">{texto}</p>

                {(onVer || onOir) && (
                    <div className="rnsl-pie">
                        {/* «Verlo ahora» NO sale si la ruta de este paso es la
                            pantalla en la que ya estás: ver `donde` arriba. */}
                        {onVer && (
                            <button type="button" className="rnsl-ver"
                                    data-prueba={prueba ? `recorrido-ver-${prueba}` : undefined}
                                    onClick={onVer}>
                                Verlo ahora →
                            </button>
                        )}
                        {onOir && (
                            <button type="button" className="rnsl-oir"
                                    data-prueba={prueba ? `recorrido-oir-${prueba}` : undefined}
                                    onClick={onOir} aria-live="polite">
                                {sonando ? '🔊 Sonando…' : '🔊 Escuchar'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
